import { useState, useEffect, useRef, useCallback } from 'react';
import { AnimatePresence } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes } from "@fortawesome/free-solid-svg-icons";

interface LightboxProps {
    selectedImage: string | null;
    images: string[];
    onClose: () => void;
}

/** Distance between two touch points */
const getTouchDistance = (touches: React.TouchList) => {
    const dx = touches[0].clientX - touches[1].clientX;
    const dy = touches[0].clientY - touches[1].clientY;
    return Math.sqrt(dx * dx + dy * dy);
};

/** Midpoint between two touch points */
const getTouchMidpoint = (touches: React.TouchList) => ({
    x: (touches[0].clientX + touches[1].clientX) / 2,
    y: (touches[0].clientY + touches[1].clientY) / 2,
});

const Lightbox = ({ selectedImage, images, onClose }: LightboxProps) => {
    const [lightboxIndex, setLightboxIndex] = useState(0);

    // Swipe state
    const [touchStart, setTouchStart] = useState(0);
    const [touchEnd, setTouchEnd] = useState(0);

    // Zoom state
    const [scale, setScale] = useState(1);
    const [translate, setTranslate] = useState({ x: 0, y: 0 });
    const pinchStartDistance = useRef(0);
    const pinchStartScale = useRef(1);
    const panStart = useRef({ x: 0, y: 0 });
    const translateStart = useRef({ x: 0, y: 0 });
    const isPinching = useRef(false);
    const isPanning = useRef(false);
    const lastTapTime = useRef(0);
    const imageRef = useRef<HTMLImageElement>(null);

    const resetZoom = useCallback(() => {
        setScale(1);
        setTranslate({ x: 0, y: 0 });
        isPinching.current = false;
        isPanning.current = false;
    }, []);

    useEffect(() => {
        if (selectedImage) {
            setLightboxIndex(images.indexOf(selectedImage));
            document.body.style.overflow = 'hidden';
            resetZoom();
        } else {
            document.body.style.overflow = '';
        }

        return () => {
            document.body.style.overflow = '';
        };
    }, [selectedImage, images, resetZoom]);

    useEffect(() => {
        const handleKeyPress = (e: KeyboardEvent) => {
            if (!selectedImage) return;

            switch (e.key) {
                case 'ArrowLeft':
                    resetZoom();
                    setLightboxIndex(prev =>
                        prev === 0 ? images.length - 1 : prev - 1
                    );
                    break;
                case 'ArrowRight':
                    resetZoom();
                    setLightboxIndex(prev =>
                        prev === images.length - 1 ? 0 : prev + 1
                    );
                    break;
                case 'Escape':
                    onClose();
                    break;
            }
        };

        if (selectedImage) {
            window.addEventListener('keydown', handleKeyPress);
        }

        return () => {
            window.removeEventListener('keydown', handleKeyPress);
        };
    }, [selectedImage, images.length, onClose, resetZoom]);

    // --- Touch handlers ---
    const handleTouchStart = (e: React.TouchEvent) => {
        if (e.touches.length === 2) {
            // Pinch start
            isPinching.current = true;
            pinchStartDistance.current = getTouchDistance(e.touches);
            pinchStartScale.current = scale;
        } else if (e.touches.length === 1) {
            if (scale > 1) {
                // Pan start (when zoomed)
                isPanning.current = true;
                panStart.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
                translateStart.current = { ...translate };
            } else {
                // Swipe start (when not zoomed)
                setTouchStart(e.touches[0].clientX);
                setTouchEnd(e.touches[0].clientX);
            }
        }
    };

    const handleTouchMove = (e: React.TouchEvent) => {
        if (e.touches.length === 2 && isPinching.current) {
            // Pinch zoom
            const distance = getTouchDistance(e.touches);
            const newScale = Math.min(4, Math.max(1, pinchStartScale.current * (distance / pinchStartDistance.current)));
            setScale(newScale);

            if (newScale <= 1) {
                setTranslate({ x: 0, y: 0 });
            }
        } else if (e.touches.length === 1 && isPanning.current && scale > 1) {
            // Pan while zoomed
            const dx = e.touches[0].clientX - panStart.current.x;
            const dy = e.touches[0].clientY - panStart.current.y;
            setTranslate({
                x: translateStart.current.x + dx,
                y: translateStart.current.y + dy,
            });
        } else if (e.touches.length === 1 && scale <= 1) {
            // Swipe tracking
            setTouchEnd(e.touches[0].clientX);
        }
    };

    const handleTouchEnd = (e: React.TouchEvent) => {
        if (isPinching.current) {
            isPinching.current = false;
            if (scale <= 1.05) resetZoom();
            return;
        }

        if (isPanning.current) {
            isPanning.current = false;
            return;
        }

        // Double-tap to toggle zoom
        const now = Date.now();
        if (now - lastTapTime.current < 300 && e.changedTouches.length === 1) {
            if (scale > 1) {
                resetZoom();
            } else {
                setScale(2.5);
            }
            lastTapTime.current = 0;
            return;
        }
        lastTapTime.current = now;

        // Swipe navigation (only when not zoomed)
        if (scale <= 1) {
            const swipeThreshold = 50;
            const swipeDistance = touchStart - touchEnd;

            if (Math.abs(swipeDistance) > swipeThreshold) {
                resetZoom();
                if (swipeDistance > 0) {
                    setLightboxIndex(prev =>
                        prev === images.length - 1 ? 0 : prev + 1
                    );
                } else {
                    setLightboxIndex(prev =>
                        prev === 0 ? images.length - 1 : prev - 1
                    );
                }
            }
        }
    };

    if (!selectedImage) return null;

    const currentMedia = images[lightboxIndex];
    const isVideo = currentMedia?.endsWith('.webm') || currentMedia?.endsWith('.mp4') || currentMedia?.endsWith('.mov');

    return (
        <AnimatePresence>
            <div
                className="fixed inset-0 bg-black/90 flex items-center justify-center z-50"
                onClick={onClose}
            >
                <div className="relative w-full h-full flex items-center justify-center">
                    <button
                        className="absolute top-4 right-4 text-white text-2xl z-10 w-10 h-10 flex items-center justify-center"
                        onClick={onClose}
                    >
                        <FontAwesomeIcon icon={faTimes} />
                    </button>

                    {isVideo ? (
                        <video
                            src={currentMedia}
                            className="max-h-[80vh] max-w-full"
                            controls
                            autoPlay
                            loop
                            muted
                            playsInline
                            controlsList="nodownload nofullscreen noremoteplayback"
                            disablePictureInPicture
                            onTouchStart={handleTouchStart}
                            onTouchMove={handleTouchMove}
                            onTouchEnd={handleTouchEnd}
                            onClick={(e) => e.stopPropagation()}
                            style={{ pointerEvents: 'auto' }}
                            onVolumeChange={(e) => {
                                const video = e.currentTarget;
                                if (video.muted === false) {
                                    video.muted = true;
                                }
                            }}
                        />
                    ) : (
                        <img
                            ref={imageRef}
                            src={currentMedia}
                            alt="Product detail"
                            className="max-h-[80vh] max-w-full select-none"
                            style={{
                                transform: `scale(${scale}) translate(${translate.x / scale}px, ${translate.y / scale}px)`,
                                transition: isPinching.current || isPanning.current ? 'none' : 'transform 0.2s ease-out',
                                touchAction: 'none',
                            }}
                            onTouchStart={handleTouchStart}
                            onTouchMove={handleTouchMove}
                            onTouchEnd={handleTouchEnd}
                            onClick={(e) => e.stopPropagation()}
                            draggable={false}
                        />
                    )}

                    {/* Dots indicator */}
                    <div className="absolute bottom-6 left-1/2 transform -translate-x-1/2 flex gap-2">
                        {images.map((_, index) => (
                            <button
                                key={index}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    resetZoom();
                                    setLightboxIndex(index);
                                }}
                                className={`h-2 rounded-full transition-all ${
                                    index === lightboxIndex
                                        ? 'w-6 bg-brand-blue'
                                        : 'w-2 bg-white/50'
                                }`}
                            />
                        ))}
                    </div>

                    {/* Zoom hint */}
                    {!isVideo && scale <= 1 && (
                        <div className="absolute bottom-14 left-1/2 -translate-x-1/2 text-white/40 text-xs md:hidden">
                            Double-tap or pinch to zoom
                        </div>
                    )}
                </div>
            </div>
        </AnimatePresence>
    );
};

export default Lightbox;
